import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import {
  cardholderBlonixId,
  cardholderDisplayName,
  formatCardholderContact,
} from "@/lib/cardholderContact";
import { ADMIN } from "@/lib/routes";
import { Field, Modal, fmtDelta } from "@/pages/Merchants";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = ["Overview", "Funding", "Cards", "Cardholders", "Transactions", "Audit"];

export default function MerchantDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState({ cards: [], users: [], tx: [], audit: [] });
  const [balanceModal, setBalanceModal] = useState(null);
  const [balanceAmt, setBalanceAmt] = useState("");
  const [balanceNote, setBalanceNote] = useState("");
  const [balanceSubmitting, setBalanceSubmitting] = useState(false);
  const balanceLock = useRef(false);
  const [funding, setFunding] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/merchants/${id}`);
    setM(r.data);
    const [c, u, t, a, f] = await Promise.all([
      api.get(`/admin/cards?merchant_id=${id}`),
      api.get(`/admin/users?merchant_id=${id}`),
      api.get(`/admin/transactions?merchant_id=${id}`),
      api.get(`/admin/audit-logs?merchant_id=${id}`),
      api.get(`/admin/merchants/${id}/balance-funding`),
    ]);
    setData({ cards: c.data.items, users: u.data.items, tx: t.data.items, audit: a.data.items });
    setFunding(f.data.items ?? []);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openBalanceModal = (mode) => {
    setBalanceModal({ mode });
    setBalanceAmt("");
    setBalanceNote("");
  };

  const onBalanceSubmit = async (e) => {
    e.preventDefault();
    if (balanceLock.current || balanceSubmitting || !balanceModal) return;
    const amount = Number(balanceAmt);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    const path = balanceModal.mode === "debit" ? "debit-balance" : "fund-balance";
    balanceLock.current = true;
    setBalanceSubmitting(true);
    try {
      await api.post(`/admin/merchants/${id}/${path}`, {
        amount,
        note: balanceNote.trim() || undefined,
      });
      toast.success(
        balanceModal.mode === "debit" ? `Debited ${fmt$(amount)}` : `Funded ${fmt$(amount)}`,
      );
      setBalanceModal(null);
      setBalanceAmt("");
      setBalanceNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Balance update failed");
    } finally {
      balanceLock.current = false;
      setBalanceSubmitting(false);
    }
  };

  const closeBalanceModal = () => {
    if (balanceSubmitting) return;
    setBalanceModal(null);
    setBalanceAmt("");
    setBalanceNote("");
  };

  if (!m) return <Layout><div className="p-10 text-sm text-zinc-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="merchant-detail-page">
        <Link to={ADMIN.merchants} data-testid="back-to-merchants" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3">
          <ArrowLeft size={12} /> Merchants
        </Link>

        <PageHeader
          title={m.company_name}
          subtitle={m.email}
          actions={
            <>
              <StatusPill value={m.kyb_status} />
              <StatusPill value={m.status} />
              <button onClick={() => openBalanceModal("fund")} data-testid="credit-balance-btn" className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Fund balance</button>
              <button onClick={() => openBalanceModal("debit")} data-testid="debit-balance-btn" className="h-9 px-4 text-sm border border-red-200 text-red-800 rounded-sm hover:bg-red-50 transition-colors duration-150">Debit balance</button>
            </>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KV label="Balance" value={<span className="font-mono tabular-nums">{fmt$(m.balance)}</span>} />
          <KV label="Cards" value={m.stats?.cards ?? 0} />
          <KV label="Cardholders" value={m.stats?.users ?? 0} />
        </div>

        <div className="border-b border-zinc-200 mb-4 overflow-x-auto" data-testid="merchant-tabs">
          <div className="flex gap-4 sm:gap-6 min-w-max sm:min-w-0">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t.toLowerCase()}`}
                className={`-mb-px px-1 py-2.5 text-sm font-medium border-b-2 transition-colors duration-150 ${
                  tab === t ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {tab === "Funding" && (
          <Surface>
            <Table cols={["Date", "Amount", "Balance before", "Balance after", "Note"]}>
              {funding.map((f) => (
                <tr key={f.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs text-zinc-500">{(f.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className={`px-5 py-3 text-right font-mono tabular-nums ${Number(f.amount) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {fmtDelta(f.amount)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(f.balance_before)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(f.balance_after)}</td>
                  <td className="px-5 py-3 text-xs text-zinc-600">{f.note || "—"}</td>
                </tr>
              ))}
              {funding.length === 0 && <Empty colSpan={5} />}
            </Table>
          </Surface>
        )}

        {tab === "Overview" && (
          <>
            <Surface className="p-5 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <KV label="API key (masked)" value={<span className="font-mono">{m.api_key_masked || "—"}</span>} />
                <KV label="Webhook URL" value={m.webhook_url || "—"} />
                <KV label="Allowed features" value={(m.allowed_features || []).join(", ") || "—"} />
                <KV label="Currency" value={m.currency} />
                <KV label="Created" value={(m.created_at || "").slice(0, 19).replace("T", " ")} />
                <KV label="Updated" value={(m.updated_at || "").slice(0, 19).replace("T", " ")} />
              </div>
            </Surface>

            {(m.issuance_grace?.length ?? 0) > 0 && (
              <Surface>
                <div className="px-5 py-4 border-b border-zinc-100">
                  <h3 className="text-sm font-medium text-zinc-900">Card issuance grace</h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    Grace pools by rule scope (card product, all virtual, or all physical). Changing the allowance does not reset cards already used.
                  </p>
                </div>
                <Table cols={["Scope", "Card types", "Issuance fee", "Free allowance", "Used", "Remaining"]}>
                  {m.issuance_grace.map((g) => (
                    <tr key={g.scope_key} className="border-b border-zinc-50">
                      <td className="px-5 py-3 font-medium capitalize">{g.scope_label}</td>
                      <td className="px-5 py-3 text-xs text-zinc-600">{(g.card_types || []).join(", ") || "—"}</td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(g.issuance_fee)}</td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">{g.free_cards}</td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">{g.cards_used}</td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">
                        {g.remaining_free_cards > 0 ? (
                          <span className="text-emerald-700">{g.remaining_free_cards}</span>
                        ) : (
                          <span className="text-zinc-400">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              </Surface>
            )}
          </>
        )}

        {tab === "Cards" && (
          <Surface>
            <Table cols={["Masked PAN", "Type", "Status", "Balance", "Created"]}>
              {data.cards.map((c) => (
                <tr key={c.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{c.masked_pan}</td>
                  <td className="px-5 py-3">{c.type}</td>
                  <td className="px-5 py-3"><StatusPill value={c.status} /></td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(c.balance)}</td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{(c.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {data.cards.length === 0 && <Empty colSpan={5} />}
            </Table>
          </Surface>
        )}

        {tab === "Cardholders" && (
          <Surface>
            <Table cols={["Cardholder ID", "Name", "Email", "Phone", "KYC", "Created"]}>
              {data.users.map((u) => (
                <tr key={u.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{cardholderBlonixId(u)}</td>
                  <td className="px-5 py-3 font-medium">{cardholderDisplayName(u)}</td>
                  <td className="px-5 py-3 text-xs">{formatCardholderContact(u)}</td>
                  <td className="px-5 py-3 text-xs">{u.phone || "—"}</td>
                  <td className="px-5 py-3">
                    <Link to={ADMIN.userKyc(u.id)} className="hover:underline">
                      <StatusPill value={u.kyc_status} />
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {data.users.length === 0 && <Empty colSpan={6} />}
            </Table>
          </Surface>
        )}

        {tab === "Transactions" && (
          <Surface>
            <Table cols={["Type", "Merchant", "Amount", "Status", "Date"]}>
              {data.tx.map((t) => (
                <tr key={t.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3">{t.type}</td>
                  <td className="px-5 py-3 text-xs">{t.merchant_name || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(t.amount)}</td>
                  <td className="px-5 py-3"><StatusPill value={t.status} /></td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
              {data.tx.length === 0 && <Empty colSpan={5} />}
            </Table>
          </Surface>
        )}

        {tab === "Audit" && (
          <Surface>
            <Table cols={["Action", "Method", "IP", "Status", "When"]}>
              {data.audit.map((a) => (
                <tr key={a.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{a.action}</td>
                  <td className="px-5 py-3"><span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-100">{a.method}</span></td>
                  <td className="px-5 py-3 font-mono text-xs">{a.ip_address || "—"}</td>
                  <td className="px-5 py-3 font-mono text-xs">{a.status_code}</td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
              {data.audit.length === 0 && <Empty colSpan={5} />}
            </Table>
          </Surface>
        )}

        {balanceModal && (
          <Modal
            title={balanceModal.mode === "debit" ? "Debit merchant balance" : "Fund merchant balance"}
            onClose={closeBalanceModal}
          >
            <form onSubmit={onBalanceSubmit} className="space-y-3">
              <p className="text-sm text-zinc-600">
                Current balance: <span className="font-mono tabular-nums font-medium">{fmt$(m.balance)}</span>
              </p>
              <Field label="Amount (USD)" testId="balance-amount-input" value={balanceAmt} onChange={setBalanceAmt} type="number" disabled={balanceSubmitting} />
              <Field label="Note (optional)" testId="balance-note-input" value={balanceNote} onChange={setBalanceNote} disabled={balanceSubmitting} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" disabled={balanceSubmitting} onClick={closeBalanceModal} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none">Cancel</button>
                <button
                  type="submit"
                  disabled={balanceSubmitting}
                  data-testid="submit-balance-btn"
                  className={`h-9 px-4 text-sm text-white rounded-sm transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none ${
                    balanceModal.mode === "debit" ? "bg-red-700 hover:bg-red-800" : "bg-zinc-950 hover:bg-zinc-800"
                  }`}
                >
                  {balanceSubmitting
                    ? balanceModal.mode === "debit"
                      ? "Debiting…"
                      : "Funding…"
                    : balanceModal.mode === "debit"
                      ? "Debit balance"
                      : "Fund balance"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}

function Table({ cols, children }) {
  return (
    <div className="table-scroll">
      <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
          {cols.map((c) => (
            <th
              key={c}
              className={`px-5 py-3 ${
                ["Amount", "Balance", "Issuance fee", "Free allowance", "Used", "Remaining"].includes(c)
                  ? "text-right"
                  : ""
              }`}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    </div>
  );
}

function Empty({ colSpan }) {
  return <tr><td colSpan={colSpan} className="px-5 py-10 text-center text-xs text-zinc-500">No data.</td></tr>;
}
